/* eslint-disable no-bitwise */
import _ from 'underscore';
import { isXandrServing, isLinkedInCampaign } from '@bingads-webui-campaign-react/constants';

import { getEditorialErrorMessage } from '@bingads-webui-campaign/api-errors';
import { getLocationString } from './location-Enum-To-String-Mapping';
import editorialTemplate from './editorial-reason.pug';
import { ENTITY, ENTITYToI18, ENTITYPluralToI18 } from './constants';

function getRejectionFooterText(i18n, entity, isAppealable, isDisclaimerDisapproved, hideParentLevel) {
  const thisEntity = i18n.getString('EditorialAppeal_ThisEntity')
    .replace('{0}', i18n.getString(ENTITYToI18[entity]));
  const allEntitiesInThisAdGroup = i18n.getString('EditorialAppeal_AllEntitiesInThisAdGroup')
    .replace('{0}', i18n.getString(ENTITYPluralToI18[entity]));
  const allEntitiesInThisCampaign = i18n.getString('EditorialAppeal_AllEntitiesInThisCampaign')
    .replace('{0}', i18n.getString(ENTITYPluralToI18[entity]));
  const allEntitiesInThisAd = i18n.getString('EditorialAppeal_AllEntitiesInThisAd').replace('{0}', i18n.getString(ENTITYPluralToI18[entity]));
  const requestExceptionForThisEntity = i18n.getString('Request_Exception').replace('{0}', thisEntity);
  let requestExceptionLink = `<a id='requestException' href='javascript:void(0);' class='clickable ${isAppealable ? '' : 'disabled'}'>${requestExceptionForThisEntity}</a>`;
  const requestExceptionForThisParentEntity = i18n.getString('Request_Exception').replace('{0}', entity === ENTITY.ASSETGROUP ? allEntitiesInThisCampaign : allEntitiesInThisAdGroup);
  let requestParentLevelExceptionLink = hideParentLevel ? null : `<a id='requestExceptionAdgroupLevel' href='javascript:void(0);' class='clickable'>${requestExceptionForThisParentEntity}</a>`;
  const helpText = i18n.getString('Fix_Disapproved_Entity_Help').replace('{0}', isDisclaimerDisapproved ? i18n.getString('DisclaimerLabelText') : i18n.getString(ENTITYToI18[entity]));
  if (entity === ENTITY.EXTENSION) {
    requestExceptionLink = `<a id='requestExceptionforExtension' href='javascript:void(0);' class='clickable ${isAppealable ? '' : 'disabled'}'>${isDisclaimerDisapproved ? i18n.getString('Request_Exception').replace('{0}', i18n.getString('DisclaimerLabelText')) : requestExceptionForThisEntity}</a>`;
    requestParentLevelExceptionLink = null;
  }
  if (entity === ENTITY.ASSET) {
    requestExceptionLink = `<a id='requestException' href='javascript:void(0);' class='clickable ${isAppealable ? '' : 'disabled'}'>${i18n.getString('Request_Exception').replace('{0}', hideParentLevel ? allEntitiesInThisAd : allEntitiesInThisAdGroup)}</a>`;
    requestParentLevelExceptionLink = null;
  }
  return {
    requestExceptionLink,
    requestParentLevelExceptionLink,
    helpText,
  };
}

function getRejectionStaticLinkFooterText(i18n) {
  const contactSupportLink = `<a href="http://go.microsoft.com/fwlink/?LinkId=506728" target="_apexPlatformHelp">${i18n.getString('Contact_Support')}</a>`;
  return {
    contactSupportLink,
    requestReviewText: i18n.getString('Request_Another_Review'),
  };
}

export function getEditorialTemplates({
  reasons,
  errI18n,
  i18n,
  locationI18n,
  commonI18n,
  entity,
  deliveryStatus,
  ad,
  keyword,
  isAppealable,
  permissions,
  isDetectionRejectionTypeEnabled,
  logActivity,
  hideParentLevel,
  hideLocation,
  scope,
}) {
  const updatedReasons = [];
  const isXandrAd = ENTITY.AD === entity && isXandrServing(
    _.chain(ad).result('Campaign').result('CampaignSubType').value(),
    _.chain(ad).result('Campaign').result('CampaignSettings').value()
  );
  const { adGroupId } = (scope && scope.values) || {};
  const isAssetAppealEnable = permissions && permissions.dynamic.AssetBasedEditorialInlineAppeal && ENTITY.ASSET === entity && adGroupId;
  const isLinkedinAd = ENTITY.AD === entity && isLinkedInCampaign(_.chain(ad).result('Campaign').result('CampaignType').value());
  const isPmaxAppealEnable = ENTITY.ASSETGROUP === entity && permissions && permissions.dynamic && permissions.dynamic.PmaxAppealEnable;
  const isAdExtensionInlineAppeal = ENTITY.EXTENSION === entity && permissions && permissions.dynamic && permissions.dynamic.AdExtensionInlineAppeal;
  const showRequestExceptionLink = ((!isXandrAd && !isLinkedinAd) && _.contains([ENTITY.AD, ENTITY.KEYWORD], entity)) || isPmaxAppealEnable || isAdExtensionInlineAppeal || isAssetAppealEnable;
  const isCountryListEnabled = permissions && permissions.dynamic && permissions.dynamic.EditorialCountryList;

  if (logActivity && logActivity.signal) {
    logActivity.signal(`reject reasons of ${entity} - ${ad ? ad.Id : keyword && keyword.Id} : ${deliveryStatus} : ${JSON.stringify(reasons)}`);
  }

  _.each(reasons, (reason, i) => {
    const reasonCode = reason.ReasonCode;
    let endReason = true;

    if ((i !== reasons.length - 1) && (reasons[i + 1].ReasonCode === reasonCode)) {
      endReason = false;
    }

    if (endReason) {
      const violation = getEditorialErrorMessage(reason.ReasonCode, errI18n, null, permissions);
      let locations = '';
      let countries = '';

      const locationString = typeof (reason.Location) === 'string' ? reason.Location : getLocationString({
        location: reason.Location,
        i18n,
        commonI18n,
      });

      if (locationString && !hideLocation) {
        locations = i18n.getString('DeliveryStatus_Violcation_Location').replace('{0}', locationString);
      }

      countries =
        _.chain(reason.LanguageToCountries)
          .pluck('PublisherCountries')
          .flatten()
          .uniq()
          .map(country => (entity === ENTITY.EXTENSION ? country : locationI18n.getString(`PublisherCountry_${country}`)))
          .value();

      let violationCountry = null;
      if (countries.length === 1) {
        violationCountry = i18n.getString('DeliveryStatus_Violcation_One_Country').replace('{0}', countries[0]);
      } else {
        violationCountry = isCountryListEnabled ? i18n.getString('DeliveryStatus_Violcation_Country_WithLink', {
          number: countries.length,
          tag: '<a class="toggle-country-list" role="button" tabindex="0">',
          endtag: '</a>',
        }) :
          i18n.getString('DeliveryStatus_Violcation_Country').replace('{0}', countries.length);
      }

      updatedReasons.push({
        violation,
        locations,
        violationCountry,
        countries,
        detectionType: isDetectionRejectionTypeEnabled ? i18n.getString(`Editorial_Detection_Type_${reason.DetectionMode || 'Unknown'}`) : '',
        rejectionType: isDetectionRejectionTypeEnabled ? i18n.getString(`Editorial_Rejection_Type_${reason.RejectionMode || 'Unknown'}`) : '',
      });
    }
  });

  const isDisclaimerDisapproved = deliveryStatus === 'DisclaimerInfoRejected' || deliveryStatus === 'DisclaimerInfoApprovedLimited' || deliveryStatus === 'DisclaimerInfoRejectedAppealRejected' || deliveryStatus === 'DisclaimerInfoApprovedLimitedAppealRejected';
  const isDisapproved = deliveryStatus === 'EditorialRejected' || deliveryStatus === 'Rejected' || deliveryStatus === 'EditorialAppealRejected' || isDisclaimerDisapproved;
  let title = i18n.getString('EntityStatus_Rejected');
  switch (deliveryStatus) {
    // new string
    case 'AppealRejected':
    case 'RejectedAppealRejected':
    case 'EditorialAppealRejected':
      title = i18n.getString('Label_DeliveryStatus_EditorialAppealRejected');
      break;
    case 'ApprovedLimitedAppealRejected':
    case 'EditorialApprovedLimitedAppealRejected':
      title = i18n.getString('Label_DeliveryStatus_EditorialApprovedLimitedAppealRejected');
      break;
    case 'ApprovedLimited':
    case 'EditorialApprovedLimited':
      title = i18n.getString('EntityStatus_ApprovedLimited');
      break;
    case 'DisclaimerInfoRejected':
      title = i18n.getString('EntityStatus_DisclaimerInfoRejected');
      break;
    case 'DisclaimerInfoApprovedLimited':
      title = i18n.getString('EntityStatus_DisclaimerInfoApprovedLimited');
      break;
    case 'DisclaimerInfoRejectedAppealRejected':
      title = i18n.getString('Label_DeliveryStatus_DisclaimerInfoRejectedAppealRejected');
      break;
    case 'DisclaimerInfoApprovedLimitedAppealRejected':
      title = i18n.getString('Label_DeliveryStatus_DisclaimerInfoApprovedLimitedAppealRejected');
      break;
    default:
      break;
  }

  return editorialTemplate({
    title,
    reasons: updatedReasons,
    i18n,
    commonI18n,
    showRequestExceptionLink,
    footer: showRequestExceptionLink
      ? getRejectionFooterText(i18n, entity, isAppealable, isDisclaimerDisapproved, hideParentLevel)
      : getRejectionStaticLinkFooterText(i18n),
    isDisapproved,
    isDetectionRejectionTypeEnabled,
    isCountryListHidden: isLinkedinAd,
    isCountryListEnabled,
    countryListLabel: isCountryListEnabled ? i18n.getString('RejectionCountriesText') : '',
  });
}

export function isDeliveryStatusDisapproved(deliveryStatusDetails) {
  const editorialStatus = ['EditorialRejected', 'EditorialApprovedLimited', 'EditorialAppealRejected', 'EditorialApprovedLimitedAppealRejected'];
  return _.contains(editorialStatus, deliveryStatusDetails);
}

